/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import qs from 'query-string';

type BackOfficeQueryStrings =
  | 'idSearch'
  | 'nameSearch'
  | 'emailSearch'
  | 'domainSearch'
  | 'segment';
type SuiteQueryStrings = 'chatId';
/* Need to find a way to select only 1 type. not union */
type QueryStrings = BackOfficeQueryStrings | SuiteQueryStrings;

type BackOfficeArrayQueryStrings =
  | 'tiers'
  | 'labels'
  | 'teams'
  | 'projects'
  | 'superRoles'
  | 'statuses'
  | 'states';

type DateRangeQueryStrings = 'createdDateRange' | 'updatedDateRange';

export function useQs() {
  return [getPathnameWithCurrentQS] as const;
}

useQs.String = <K extends QueryStrings>({ key }: { key: K }) => {
  return [
    {
      [key]: qs.parse(window.location.search)[key],
    } as { [key in K]: string | null },
    { setQs: setQs(key), deleteQs: deleteQs(key) },
  ] as const;
};

useQs.Array = <K extends BackOfficeArrayQueryStrings>({ key }: { key: K }) => {
  return [
    {
      [key]: qs.parse(window.location.search)[key],
    } as { [key in K]: string[] | null },
    { setQs: setQs(key), deleteQs: deleteQs(key) },
  ] as const;
};

useQs.DateRange = <K extends DateRangeQueryStrings>({ key }: { key: K }) => {
  return [
    {
      [key]: {
        start: qs.parse(window.location.search)[`${key}.start`],
        end: qs.parse(window.location.search)[`${key}.end`],
      },
    } as { [key in K]: { start: string | null; end: string | null } },
    { setQs: setQsDateRange(key), deleteQs: deleteQsDateRange(key) },
  ] as const;
};

const setQs =
  (key: QueryStrings | BackOfficeArrayQueryStrings) =>
  (value: string | string[], pushState?: boolean) => {
    const prev = qs.parse(window.location.search);
    const url = qs.stringifyUrl(
      {
        url: window.location.href,
        query: { ...prev, [key]: value },
      },
      { skipEmptyString: true }
    );

    // Update the URL without reloading the page
    window.history.replaceState({}, '', url);

    if (pushState) {
      // Add to browser history
      window.history.pushState({}, '', url);
    }
  };

const setQsDateRange =
  (key: DateRangeQueryStrings) =>
  (value: { start: string | null; end: string | null }, pushState?: boolean) => {
    const prev = qs.parse(window.location.search);
    const url = qs.stringifyUrl(
      {
        url: window.location.href,
        query: {
          ...prev,
          [`${key}.start`]: value.start ?? '',
          [`${key}.end`]: value.end ?? '',
        },
      },
      { skipEmptyString: true }
    );

    // Update the URL without reloading the page
    window.history.replaceState({}, '', url);

    if (pushState) {
      // Add to browser history
      window.history.pushState({}, '', url);
    }
  };

const deleteQs = (key: QueryStrings | BackOfficeArrayQueryStrings) => (pushState?: boolean) => {
  const url = qs.exclude(window.location.href, (name) => name === key);

  // Update the URL without reloading the page
  window.history.replaceState({}, '', url);

  if (pushState) {
    // Add to browser history
    window.history.pushState({}, '', url);
  }
};

const deleteQsDateRange = (key: DateRangeQueryStrings) => (pushState?: boolean) => {
  const url = qs.exclude(window.location.href, (name) => name.includes(key));

  // Update the URL without reloading the page
  window.history.replaceState({}, '', url);

  if (pushState) {
    // Add to browser history
    window.history.pushState({}, '', url);
  }
};

const getPathnameWithCurrentQS = (pathname: string) => {
  const prev = qs.parse(window.location.search);

  return qs.stringifyUrl({ url: pathname, query: prev });
};
